/**
 * Catalogue pricing — standard prices and the between-break quick-calc.
 *
 * Each quantity break is a pre-calculated *standard price* (a total £). For an
 * in-between quantity we price at the **higher bracketing per-unit** (the lower
 * break's per-unit, since per-unit falls with volume) — instant and conservative,
 * never under-quoting. Only below the smallest or above the largest break is the
 * estimate flagged as indicative. See product-catalogue/02_ui_sketches.md.
 *
 * This is the *base estimate* surrogate for the catalogue; business pricing
 * adjustments (rush / commitment / market) layer over it later.
 */

import type { QuantityBreak } from "../data/catalogue-types";

export type PriceBasis = "standard" | "between" | "below" | "above";

export interface PriceResult {
  /** Total £ for the requested quantity. */
  price: number;
  /** £ per unit at the requested quantity. */
  perUnit: number;
  basis: PriceBasis;
  /** True when the price is indicative and needs the full estimate to confirm. */
  indicative: boolean;
}

function perUnitOf(b: QuantityBreak): number {
  return b.qty > 0 ? b.price / b.qty : 0;
}

/**
 * Price a quantity against an item's standard breaks.
 *
 * Assumes `breaks` is non-empty. Exact match → standard price; between two
 * breaks → the lower break's per-unit (conservative); below the smallest →
 * smallest break's per-unit (indicative); above the largest → largest break's
 * per-unit (indicative — really needs a full estimate).
 */
export function priceForQuantity(
  breaks: QuantityBreak[],
  qty: number,
): PriceResult {
  const sorted = [...breaks].sort((a, b) => a.qty - b.qty);
  const exact = sorted.find((b) => b.qty === qty);
  if (exact) {
    return {
      price: exact.price,
      perUnit: perUnitOf(exact),
      basis: "standard",
      indicative: false,
    };
  }
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (qty < first.qty) {
    const perUnit = perUnitOf(first);
    return {
      price: Math.round(qty * perUnit),
      perUnit,
      basis: "below",
      indicative: true,
    };
  }
  if (qty > last.qty) {
    const perUnit = perUnitOf(last);
    return {
      price: Math.round(qty * perUnit),
      perUnit,
      basis: "above",
      indicative: true,
    };
  }
  // Between two breaks — use the lower break's (higher) per-unit.
  const lower = [...sorted].reverse().find((b) => b.qty < qty) ?? first;
  const perUnit = perUnitOf(lower);
  return {
    price: Math.round(qty * perUnit),
    perUnit,
    basis: "between",
    indicative: false,
  };
}

/** The "from" price for a grid tile — the standard price at the default quantity. */
export function fromPrice(
  breaks: QuantityBreak[],
  defaultQuantity: number,
): number {
  return priceForQuantity(breaks, defaultQuantity).price;
}
